import { FlowGrid, InfoSheetPanel, Paragraph, useOverlay } from '@fs/zion-ui'
import PropTypes from 'prop-types'
import React, { useCallback, useMemo, useReducer, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getNestedOrFloatingElementById, useSlsStateContext, getNestedSubelementsByType } from '@fs/zion-sls'
import { useOverlayContext } from '../../../../../shared/providers/overlayContextProvider/OverlayContext'
import { useSharedBrowserStateContext } from '../../../../../shared/providers/sharedBrowserProvider/SharedBrowserContext'
import { useR2StateContext } from '../../../../providers/r2Provider/R2Context'
import { EssentialInformationCard } from '../../../../components/generic/essentialInformationCard'
import { FamilyMembersCard } from '../../../../components/generic/familyMembersCard/FamilyMembersCard'
import { stepperInitialState, stepperReducer, STEPS } from '../../../../utils/stepperReducer'
import EventsCard from '../../../../components/generic/eventsCard/EventsCard'
import AdditionalFactsCard from '../../../../components/generic/additionalFactsCard/AdditionalFactsCard'
import { ADD_NAME_PANEL_REFERRERS, getHeaderDetails, redirectToSourceLinker } from './utils/utils'
import { ReviewCard } from '../../../../components/generic/reviewCard/ReviewCard'
import useUnsavedChangesOverlay from '../../../../hooks/useUnsavedChangesOverlay'
import UnsavedChangesDialog from '../../../../components/generic/unsavedChangesDialog/UnsavedChangesDialog'
import { useDisplayHighlights } from '../../../../hooks/useDisplayHighlights'
import { MAKE_NEW_RECORD } from '../../../../components/generic/familyMembersCard/utils/saveRelationships'

/**
 * AddNamesPanel component for managing the step-by-step process of adding and reviewing person details,
 * including essential information, family members, events, and additional facts. Handles unsaved changes,
 * overlays, and navigation between multiple persons to review.
 *
 * @param {Object} props - Component props.
 * @param {Object} [props.referrer=ADD_NAME_PANEL_REFERRERS.BLANK_STATE] - The source referrer for the panel, determining header details.
 * @param {string} [props.recordId] - An existing record id to add the new person to. Optional.
 * @param {boolean} [props.disableFamilyMemberTracking=true] - When true, disables tracking and review of newly created family members.
 * @param {function} [props.setSelectedRecordId] - Optional callback to set the selected record id in the parent component.
 *
 * @returns {JSX.Element} The rendered panel UI for adding and reviewing person details.
 */
const AddNamesPanel = ({ referrer = ADD_NAME_PANEL_REFERRERS.BLANK_STATE, recordId = null, setSelectedRecordId }) => {
  const [t] = useTranslation()
  const { images } = useSlsStateContext()
  const { addNamesOverlay, closeOverlay, closeAll } = useOverlayContext()
  const { thumbnails } = useSharedBrowserStateContext()
  const r2State = useR2StateContext()
  const [stepperState, stepperDispatch] = useReducer(stepperReducer, stepperInitialState)
  const [personId, setPersonId] = useState(null)
  const saveChangesOverlay = useOverlay()
  const [showSaveChangesOverlay, setShowSaveChangesOverlay] = useState(false)
  const scrollRef = useRef(null)
  const splitScreenPartitionKey = r2State.splitScreen ? thumbnails?.[r2State?.referenceFocusedIndex]?.ark : null
  const record = useMemo(() => {
    if (!recordId) return null
    return getNestedOrFloatingElementById(images, recordId, true)
  }, [images, recordId])
  const householdHasOtherPersons = useMemo(
    () => record?.subElements?.some((sub) => sub?.elementType === 'PERSON' && sub?.id !== personId) || false,
    [record, personId]
  )

  // Retrieve the SLS person object by id
  // on the first load, if no person id is provided, create a new person object
  // Don't set primary here - let prepareEssentialInformationDefaultValues determine it based on household context
  const slsPerson = useMemo(
    () => (personId ? getNestedOrFloatingElementById(images, personId) : { recordId: recordId || MAKE_NEW_RECORD }),
    [images, recordId, personId]
  )

  const headerDetails = getHeaderDetails(referrer)

  const closeAndReset = useCallback(
    (shouldCloseAll = false) => {
      stepperDispatch({ type: 'RESET' })
      setPersonId(null)
      setSelectedRecordId?.(null)
      if (shouldCloseAll) closeAll()
      else closeOverlay('addNamesOverlay')
    },
    [closeOverlay, closeAll, setSelectedRecordId]
  )

  const handleClosePanel = useCallback(
    (shouldCloseAll = false) => {
      if (showSaveChangesOverlay) {
        saveChangesOverlay.open()
      } else {
        closeAndReset(shouldCloseAll)
      }
    },
    [showSaveChangesOverlay, saveChangesOverlay, closeAndReset]
  )

  const handleDirtyChange = useCallback((isDirty) => {
    setShowSaveChangesOverlay(isDirty)
  }, [])

  useUnsavedChangesOverlay({
    overlay: saveChangesOverlay,
    panelId: 'addNamesOverlay',
    condition: showSaveChangesOverlay,
  })

  const sharedOnSaveCallback = useCallback(
    (currentStep, nextStep) => {
      const actions = [{ type: 'SET_REVIEWED', step: currentStep, value: true }]
      if (stepperState.currentEditStep === currentStep) {
        // Advance to the next step in flow
        actions.push({ type: 'SET_CURRENT_STEP', step: nextStep })
      } else {
        // Close the card and let the reducer figure out what card to open next
        actions.push({ type: 'SET_EDIT_MODE', step: currentStep, value: false })
      }
      stepperDispatch({
        type: 'BATCH',
        actions,
      })

      setShowSaveChangesOverlay(false)
    },
    [stepperState.currentEditStep]
  )

  // Display highlights when all cards are in view mode
  const allCardsInViewMode = useMemo(
    () => Object.values(stepperState.editMode).every((isEditing) => !isEditing),
    [stepperState.editMode]
  )

  const fieldIds = useMemo(() => {
    if (!slsPerson?.id) return []
    const fields = getNestedSubelementsByType(slsPerson, 'FIELD')
    return fields.map((field) => field.id).filter(Boolean)
  }, [slsPerson])

  useDisplayHighlights({
    elementIds: fieldIds,
    enabled: allCardsInViewMode && !!slsPerson?.id, // Only when all cards in view mode and person exists
  })

  return (
    <InfoSheetPanel {...addNamesOverlay} close={() => handleClosePanel()} {...headerDetails}>
      {/* Using the person id as key to force re-render when person changes, so all child components reset their internal state */}
      <FlowGrid columnCount={1} orientation="horizontal" key={slsPerson?.id}>
        <Paragraph size="sm" ref={scrollRef}>
          {t(
            'fullText.addNamesPanel.description',
            'Enter document details like names, relationships, and events for the person you would like to add.'
          )}
        </Paragraph>
        <EssentialInformationCard
          person={slsPerson}
          reviewed={stepperState.reviewed.essentialInfo}
          editMode={stepperState.editMode[STEPS.ESSENTIAL_INFO]}
          setEditMode={(value) => stepperDispatch({ type: 'SET_EDIT_MODE', step: STEPS.ESSENTIAL_INFO, value })}
          setReviewed={(value) => stepperDispatch({ type: 'SET_REVIEWED', step: STEPS.ESSENTIAL_INFO, value })}
          onSave={({ personId: newPersonID }) => {
            setPersonId(newPersonID)
            sharedOnSaveCallback(STEPS.ESSENTIAL_INFO, STEPS.EVENTS)
          }}
          onDirtyChange={handleDirtyChange}
          saveCardDataToSls
          splitScreenPartitionKey={splitScreenPartitionKey}
        />
        <EventsCard
          person={slsPerson}
          reviewed={stepperState.reviewed.events}
          editMode={stepperState.editMode[STEPS.EVENTS]}
          setEditMode={(value) => stepperDispatch({ type: 'SET_EDIT_MODE', step: STEPS.EVENTS, value })}
          setReviewed={(value) => stepperDispatch({ type: 'SET_REVIEWED', step: STEPS.EVENTS, value })}
          onSave={() => {
            sharedOnSaveCallback(STEPS.EVENTS, STEPS.ADDITIONAL_FACTS)
          }}
          saveCardDataToSls
          onDirtyChange={handleDirtyChange}
          showDataInPendingState={false}
          splitScreenPartitionKey={splitScreenPartitionKey}
        />
        <AdditionalFactsCard
          person={slsPerson}
          reviewed={stepperState.reviewed.additionalFacts}
          editMode={stepperState.editMode[STEPS.ADDITIONAL_FACTS]}
          setEditMode={(value) => stepperDispatch({ type: 'SET_EDIT_MODE', step: STEPS.ADDITIONAL_FACTS, value })}
          setReviewed={(value) => stepperDispatch({ type: 'SET_REVIEWED', step: STEPS.ADDITIONAL_FACTS, value })}
          onSave={() => {
            const nextStep = householdHasOtherPersons ? STEPS.FAMILY_MEMBERS : STEPS.REVIEW_ALL
            sharedOnSaveCallback(STEPS.ADDITIONAL_FACTS, nextStep)
          }}
          onDirtyChange={handleDirtyChange}
          saveCardDataToSls
          splitScreenPartitionKey={splitScreenPartitionKey}
        />
        {householdHasOtherPersons && (
          <FamilyMembersCard
            person={slsPerson}
            reviewed={stepperState.reviewed.familyMembers}
            editMode={stepperState.editMode[STEPS.FAMILY_MEMBERS]}
            setEditMode={(value) => stepperDispatch({ type: 'SET_EDIT_MODE', step: STEPS.FAMILY_MEMBERS, value })}
            setReviewed={(value) => stepperDispatch({ type: 'SET_REVIEWED', step: STEPS.FAMILY_MEMBERS, value })}
            onSave={() => {
              sharedOnSaveCallback(STEPS.FAMILY_MEMBERS, STEPS.REVIEW_ALL)
            }}
            isInReviewProcess={false}
            onDirtyChange={handleDirtyChange}
            saveCardDataToSls
            splitScreenPartitionKey={splitScreenPartitionKey}
            // This will make the relationship inputs empty by default, so the user goes and selects the correct relationship for each family member
            // instead of defaulting to "No relation"
            isCreatingNewPerson
          />
        )}
        <ReviewCard
          active={stepperState.editMode[STEPS.REVIEW_ALL]}
          person={slsPerson}
          splitScreenPartitionKey={splitScreenPartitionKey}
          onSave={(action) => {
            if (action === 'attach') {
              const personArk = slsPerson.id || slsPerson.recordId
              redirectToSourceLinker(personArk)
              handleClosePanel()
            } else if (action === 'closeAll') {
              handleClosePanel(true)
            } else if (action === 'close') {
              handleClosePanel()
            }
          }}
        />
      </FlowGrid>
      {/* Unsaved Changes Overlay */}
      <UnsavedChangesDialog
        overlay={saveChangesOverlay}
        onExit={() => {
          saveChangesOverlay.close()
          // If there's a pending action, run it after confirming exit, e.g., navigate away or close panel
          if (typeof saveChangesOverlay.pendingAction === 'function') {
            saveChangesOverlay.pendingAction()
          } else {
            closeAndReset()
          }
          closeOverlay('addNamesOverlay')
        }}
      />
    </InfoSheetPanel>
  )
}

AddNamesPanel.propTypes = {
  referrer: PropTypes.string,
  person: PropTypes.shape({
    id: PropTypes.string,
  }),
  recordId: PropTypes.string,
  disableFamilyMemberTracking: PropTypes.bool,
}

AddNamesPanel.defaultProps = {
  referrer: ADD_NAME_PANEL_REFERRERS.BLANK_STATE,
  person: undefined,
  recordId: null,
  disableFamilyMemberTracking: true,
}

export default AddNamesPanel
